import type { WeaponDef } from '../../sim/defs';
import { Stat } from '../../sim/stats';

/** A01 — LANCE-PLASMA. « Moins de cadence, dégâts par salve élevés »
 *  (nœud `arme-lance-plasma`, 900 crédits).
 *
 *  4 volées/s × 2 projectiles × 25 dégâts = 200 DPS — le même nombre que le
 *  VULCAN, au chiffre près. Elles ne se départagent pas en puissance.
 *
 *  ── Où l'échange se produit vraiment : la QUANTIFICATION ──────────────────
 *  À DPS nominal égal, un projectile de 25 dégâts et un de 10 ne valent pas la
 *  même chose selon la cible, et c'est là toute l'arme :
 *
 *    · MITE, 30 PV — il faut 2 projectiles au PLASMA comme 3 au VULCAN, mais le
 *      PLASMA n'en tire que 8 par seconde contre 20. Plafond théorique :
 *      4 mites/s contre 6,7. Le surplus de dégâts du dernier projectile est
 *      PERDU, et il est perdu quarante fois par vague de popcorn.
 *    · RÔDEUR, 240 PV — 10 projectiles d'un côté, 24 de l'autre, 1,25 s dans
 *      les deux cas. Rien ne se perd, les deux armes se valent exactement.
 *
 *  Le PLASMA est donc l'arme des cibles qui encaissent, et il est franchement
 *  mauvais contre les nuées. Ce n'est pas un réglage : c'est une conséquence
 *  arithmétique de sa forme, ce qui la rend stable — aucun équilibrage futur ne
 *  l'effacera par accident.
 *
 *  ── Les 250 ms entre deux volées ──────────────────────────────────────────
 *  Le vrai coût, et il ne se lit dans aucune statistique. Une cible qui traverse
 *  la ligne de tir entre deux volées n'est pas touchée du tout ; à 10 volées/s
 *  elle l'aurait été. Le PLASMA demande d'anticiper le placement, là où le
 *  VULCAN pardonne de le corriger.
 *
 *  ── Ce que la taille compense ─────────────────────────────────────────────
 *  +35 % de rayon, seule statistique où le PLASMA dépasse le VULCAN. Elle
 *  rachète une part de la latence : un projectile plus large tolère un
 *  alignement plus approximatif, ce qui est exactement ce qu'il faut à une arme
 *  qui ne peut pas se rattraper 100 ms plus tard.
 *
 *  ⚠️ La vitesse des projectiles reste à 1 800 u/s. La tentation était de la
 *  baisser — un « gros » projectile lent est le cliché du genre — mais le
 *  FAISCEAU occupe déjà cette case, et deux armes lentes sur trois auraient
 *  supprimé l'axe au lieu de le peupler. */
const plasma: WeaponDef = {
  id: 'plasma',
  name: 'Lance-plasma',
  modifiers: [
    /* ⚠️ Les deux facteurs sont MULTIPLICATIFS et leur produit vaut exactement
       1 (2,5 × 0,4). Ce n'est pas de la coquetterie : `computeStats` applique
       tous les additifs AVANT tous les multiplicatifs, si bien qu'un profil
       purement multiplicatif conserve son rapport de DPS avec le VULCAN quelles
       que soient les cartes ramassées ensuite, additives comprises. Exprimer le
       même profil en additifs (« +15 dégâts, −6 volées/s ») donnerait deux armes
       dont l'écart de DPS s'ouvre au fil de la run, sans qu'aucun test de
       l'équilibrage de départ ne puisse le voir. */
    { stat: Stat.FireRate, op: 'mul', value: -0.6 },
    { stat: Stat.Damage, op: 'mul', value: 1.5 },
    { stat: Stat.BulletRadius, op: 'mul', value: 0.35 },
  ],
  text: '4 volées/s × 2 × 25 dégâts — 200 DPS, projectiles 35 % plus larges',
};

export default plasma;
